import { Button } from "design-system/atoms/Button"
import { Plus } from "lucide-react"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BusinessOpportunityCapabilityConfigSchema } from "schema"
import z from "zod"
import { FieldsForm } from "../CreateCustomCapability/FieldsForm"

export type RequiredField = z.infer<
  typeof BusinessOpportunityCapabilityConfigSchema
>["requiredFields"][number]

type Props = {
  value: RequiredField[]
  onChange: (value: RequiredField[]) => void
}

export function BusinessOpportunityRequiredFieldsField({ value, onChange }: Props) {
  const { t } = useTranslation()

  const fieldsFormValue = useMemo(
    () =>
      value.map((f) => ({
        name: f.key,
        type: f.type ?? ("string" as const),
        description: f.description,
        required: f.required !== false,
      })),
    [value],
  )

  const handleChange = useCallback(
    (
      fields: {
        name: string
        type: RequiredField["type"]
        description?: string
        required?: boolean
      }[],
    ) => {
      onChange(
        fields.map((f) => ({
          key: f.name,
          description: f.description ?? "",
          type: (f.type ?? "string") as "string" | "number" | "boolean",
          required: f.required !== false,
        })),
      )
    },
    [onChange],
  )

  const handleAdd = () => {
    onChange([...value, { key: "", description: "", type: "string", required: true }])
  }

  return (
    <FieldsForm
      label={t("capabilities.catalog.BusinessOpportunity.config.requiredFields.label")}
      description={t("capabilities.catalog.BusinessOpportunity.config.requiredFields.description")}
      action={
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="w-4 h-4" />
          <span>
            {t("capabilities.catalog.BusinessOpportunity.config.requiredFields.addButton")}
          </span>
        </Button>
      }
      value={fieldsFormValue}
      onChange={handleChange}
      mode="schema"
    />
  )
}
